import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import SlideInPanel from '../common/SlideInPanel';
import { workService } from '../../services/workService';
import { WorkStatusBadge } from './WorkStatusBadge';
import { PriorityChip } from './PriorityChip';
import { DueDateLabel } from './DueDateLabel';
import UserBadge from '../common/UserBadge';
import { FiExternalLink } from 'react-icons/fi';

interface Props {
  taskId: string;
  onClose: () => void;
}

export const TaskDetailPanel: React.FC<Props> = ({ taskId, onClose }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { data: task } = useQuery(['task', taskId], () => workService.getTask(taskId));

  if (!task) return null;

  return (
    <SlideInPanel open title={task.title} subtitle={task.description || undefined} accentColor="violet" onClose={onClose}>
      <div className="flex gap-2 mb-4">
        <button onClick={() => { onClose(); navigate(`/tasks/${taskId}`); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <FiExternalLink className="w-3.5 h-3.5" /> Open Full Page
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <WorkStatusBadge status={task.status} size="md" />
        <PriorityChip priority={task.priority} />
        <DueDateLabel date={task.due_date} />
        <UserBadge owner={task.owner} size="sm" />
      </div>

      {/* Metadata grid */}
      <div className={`grid grid-cols-2 gap-3 mb-6`}>
        {[
          { label: 'Assignee', value: task.assignee_id ? task.assignee_id.slice(0, 8) + '...' : '—' },
          { label: 'Scheduled', value: task.scheduled_at ? new Date(task.scheduled_at).toLocaleDateString() : '—' },
          { label: 'Estimated', value: task.estimated_minutes ? `${task.estimated_minutes}m` : '—' },
          { label: 'Actual', value: task.actual_minutes ? `${task.actual_minutes}m` : '—' },
        ].map(m => (
          <div key={m.label} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{m.label}</div>
            <div className={`text-sm font-medium mt-0.5 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Entity refs */}
      {task.entity_refs && task.entity_refs.length > 0 && (
        <div>
          <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Linked Entities ({task.entity_refs.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {task.entity_refs.map(ref => (
              <span key={ref.id} className={`px-2.5 py-1 rounded-lg text-xs ${darkMode ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                {ref.entity_type}: {ref.label || ref.entity_id.slice(0, 8)}
              </span>
            ))}
          </div>
        </div>
      )}
    </SlideInPanel>
  );
};
